import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';

const OrderContext = createContext();

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};

export const OrderProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Fetch orders from backend
  const fetchOrders = async () => {
    if (!user || !token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/orders/my-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      } else {
        console.error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all orders (for admin)
  const fetchAllOrders = async () => {
    if (!user || !token || !user.isAdmin) {
      return [];
    }

    try {
      const response = await fetch(`${API_BASE_URL}/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        return await response.json();
      } else {
        console.error('Failed to fetch all orders');
        return [];
      }
    } catch (error) {
      console.error('Error fetching all orders:', error);
      return [];
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [user, token]);

  const placeOrder = async (orderData) => {
    console.log('placeOrder called with:', { user, token, orderData });
    
    if (!user || !token) {
      console.log('Missing user or token:', { user: !!user, token: !!token });
      toast.error('Please login to place an order');
      return { success: false };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const newOrder = await response.json();
        setOrders(prevOrders => [newOrder, ...prevOrders]);
        toast.success('Order placed successfully!');
        return { success: true, orderId: newOrder._id };
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to place order');
        return { success: false };
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order');
      return { success: false };
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    if (!user || !token || !user.isAdmin) {
      toast.error('Admin privileges required');
      return { success: false };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        const updatedOrder = await response.json();
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order._id === orderId ? updatedOrder : order
          )
        );
        toast.success(`Order status updated to ${status}`);
        return { success: true };
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to update order status');
        return { success: false };
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
      return { success: false };
    }
  };

  const getOrderById = (orderId) => {
    return orders.find(order => order.id === orderId);
  };

  const getUserOrders = (userId) => {
    return orders.filter(order => order.userId === userId);
  };

  const getAllOrders = async () => {
    if (!user || !user.isAdmin) {
      return orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    
    try {
      const allOrders = await fetchAllOrders();
      return allOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } catch (error) {
      console.error('Error fetching all orders:', error);
      return orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
  };

  const getOrdersByStatus = (status) => {
    return orders.filter(order => order.status === status);
  };

  const getOrderStats = async () => {
    if (!user || !user.isAdmin) {
      const totalOrders = orders.length;
      const pendingOrders = orders.filter(order => order.status === 'pending').length;
      const completedOrders = orders.filter(order => order.status === 'delivered').length;
      const totalRevenue = orders
        .filter(order => order.status === 'delivered')
        .reduce((total, order) => total + order.total, 0);
      
      return {
        totalOrders,
        pendingOrders,
        completedOrders,
        totalRevenue
      };
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/orders/stats/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        return await response.json();
      } else {
        console.error('Failed to fetch order stats');
        return {
          totalOrders: 0,
          pendingOrders: 0,
          completedOrders: 0,
          totalRevenue: 0
        };
      }
    } catch (error) {
      console.error('Error fetching order stats:', error);
      return {
        totalOrders: 0,
        pendingOrders: 0,
        completedOrders: 0,
        totalRevenue: 0
      };
    }
  };

  const deleteOrder = async (orderId) => {
    if (!user || !token || !user.isAdmin) {
      toast.error('Admin privileges required');
      return { success: false };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/orders/${orderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setOrders(prevOrders => prevOrders.filter(order => order._id !== orderId));
        toast.success('Order deleted successfully');
        return { success: true };
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to delete order');
        return { success: false };
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
      return { success: false };
    }
  };

  const value = {
    orders,
    loading,
    placeOrder,
    updateOrderStatus,
    getOrderById,
    getUserOrders,
    getAllOrders,
    getOrdersByStatus,
    getOrderStats,
    deleteOrder
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
}; 